import React from 'react';
import CheckIcon from '@mui/icons-material/Check';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import head from 'lodash/head';
import dayjs from 'utils/dayjs';
import {
  PickerDateRange,
  DateRangePreset,
  DatePreset,
  Preset,
} from 'core/components/DateTimePicker/types';

type Props<TPreset extends Preset, TValue> = {
  presets: TPreset[];
  onSelect: (value: TValue) => void;
  currentValue?: TValue;
};

export default function Presets<TPreset extends Preset, TValue>({
  presets,
  onSelect,
  currentValue,
}: Props<TPreset, TValue>) {
  const isDateRangePresets = <TPreset extends Preset>(preset: TPreset): preset is Extract<TPreset, DateRangePreset> => 'dateRange' in preset;
  const isDatePresets = <TPreset extends Preset>(preset: TPreset): preset is Extract<TPreset, DatePreset> => 'date' in preset;

  const handleClick = (preset: TPreset) => {
    if (isDateRangePresets(preset)) {
      onSelect(preset.dateRange as TValue);
    } else if (isDatePresets(preset)) {
      onSelect(preset.date as TValue);
    }
  };

  const compareDates = (date1: Date, date2: Date) => (
    dayjs(date1).startOf('day').valueOf() === dayjs(date2).startOf('day').valueOf()
  );

  const isSameDate = (
    date1: Date | undefined,
    date2: Date | undefined,
  ) => !isNil(date1) && !isNil(date2) && compareDates(date1, date2);

  const compareRanges = (range1: PickerDateRange, range2: PickerDateRange) => {
    const equalFrom = isSameDate(range1.from, range2.from);
    const equalTo = isSameDate(range1.to, range2.to);

    return equalFrom && equalTo;
  };

  const matchesCurrent = (preset: TPreset) => {
    const result: boolean[] = [];
    if (isDateRangePresets(preset)) {
      const value = currentValue as PickerDateRange | undefined;
      result.push(!isNil(value) && compareRanges(value, preset.dateRange));
    } else if (isDatePresets(preset)) {
      const value = currentValue as Date | undefined;
      result.push(!isNil(value) && compareDates(value, preset.date));
    }

    return head(result) ?? false;
  };

  return (
    <ul className="flex items-start gap-x-2 sm:flex-col">
      {presets.map((preset) => (
        <li key={`preset-${preset.label}`} className="sm:w-full sm:py-px">
          <button
            type="button"
            title={preset.label}
            className={clsx(
              'relative w-full flex items-center gap-x-1 overflow-hidden rounded-sm border px-2.5 py-1.5 text-left text-base text-ellipsis whitespace-nowrap shadow-xs outline-hidden transition-all sm:border-none sm:py-2 sm:text-sm sm:shadow-none',
              'text-gray-700',
              'border-gray-200',
              'focus-visible:bg-gray-100',
              'hover:bg-gray-100',
              { '!bg-blue-500 !text-white': matchesCurrent(preset) },
            )}
            onClick={() => handleClick(preset)}
            aria-label={`Select ${preset.label}`}
          >
            <CheckIcon className="!w-4 !h-4 !text-white" />
            <span>{preset.label}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}
